import {
  AdvanceClockResponseSchema,
  ConsoleLoginResponseSchema,
  DemoClockSchema,
  DemoGuideSchema,
  GenerateDemoQrResponseSchema,
  AgentSkillSchema,
  CreditLineDetailSchema,
  DisputePageSchema,
  DisputeSchema,
  HumanTaskSchema,
  IncomingRefundPageSchema,
  IncomingRefundSchema,
  ReconciliationExceptionSchema,
  ReconciliationRunSchema,
  type DisputeDecisionRequestSchema,
  KbArticleSchema,
  KbArticleVersionSchema,
  KbSearchHitSchema,
  type AdminDecisionRequestSchema,
  type AgentRole,
  type ApplicationPhase,
  type CreateKbArticleRequestSchema,
  type UpdateKbArticleRequestSchema,
  type DocumentVerdict,
  type EvidenceType,
  type MoneyJson,
} from '@paguz/contracts';
import { AdminApplicationDetailSchema, type AdminApplicationQuery } from './origination-schemas.ts';
import { z } from 'zod';
import {
  CustomerDetailResponseSchema,
  CustomerListResponseSchema,
  LineDetailResponseSchema,
  OperatorActionResponseSchema,
  PaymentIntentDetailResponseSchema,
  PaymentIntentListResponseSchema,
} from './real-adapters.ts';
import { AdminMetricsSchema, IntegrationHealthSchema } from './metrics-schemas.ts';
import {
  AgentUsageSchema,
  CustomerMemorySchema,
  HumanMessageResultSchema,
  SkillVersionListSchema,
  VersionedHumanTaskSchema,
  type AgentSessionQuery,
} from './agent-schemas.ts';
import {
  AdminLineDetailSchema,
  AuditPageSchema,
  AuditVerifyResultSchema,
  BrebFaultStateSchema,
  DecisionPolicyPreviewSchema,
  DemoMerchantListSchema,
  DemoResetResultSchema,
  RAW_SETTING_WIRE,
  SETTING_PATHS,
  settingResponseSchema,
  UsuryRateListSchema,
  type AdminPaymentIntentQuery,
  type AuditQuery,
  type LineStatusRequest,
  type SettingKey,
  type SettingValue,
  type Versioned,
} from './admin-schemas.ts';
import { apiRequest } from './client.ts';
import { AgentSessionListResponseSchema, AgentTraceResponseSchema } from './agent-adapters.ts';
import {
  ApplicationDetailResponseSchema,
  ApplicationListResponseSchema,
  DecisionResponseSchema,
} from './application-adapters.ts';

/** Cliente tipado de `/v1/auth/console`, `/v1/admin/*` y `/v1/demo/*` (`04-api-contratos.md` §12–13). */
export const api = {
  login: (email: string, password: string) =>
    apiRequest('/v1/auth/console/login', {
      method: 'POST',
      body: { email, password },
      schema: ConsoleLoginResponseSchema,
    }),

  // Transacciones
  listPaymentIntents: (query: AdminPaymentIntentQuery) =>
    apiRequest('/v1/admin/payment-intents', { query, schema: PaymentIntentListResponseSchema }),
  getPaymentIntent: (id: string) =>
    apiRequest(`/v1/admin/payment-intents/${encodeURIComponent(id)}`, {
      schema: PaymentIntentDetailResponseSchema,
    }),
  operatorAction: (
    id: string,
    action: 'retry' | 'query-breb' | 'force-compensation' | 'resolve',
    reason: string,
    stepId?: string,
  ) => {
    const base = `/v1/admin/payment-intents/${encodeURIComponent(id)}`;
    const path =
      action === 'retry' && stepId
        ? `${base}/steps/${encodeURIComponent(stepId)}/retry`
        : `${base}/${action}`;
    return apiRequest(path, {
      method: 'POST',
      body: action === 'resolve' ? { reason, outcome: 'ACCEPTED' } : { reason },
      idempotent: true,
      schema: OperatorActionResponseSchema,
    });
  },

  // Clientes y líneas
  listCustomers: (q: string, cursor?: string) =>
    apiRequest('/v1/admin/customers', { query: { q, cursor }, schema: CustomerListResponseSchema }),
  getCustomer: (id: string) =>
    apiRequest(`/v1/admin/customers/${encodeURIComponent(id)}`, {
      schema: CustomerDetailResponseSchema,
    }),
  getLine: (id: string) =>
    apiRequest(`/v1/admin/lines/${encodeURIComponent(id)}`, { schema: LineDetailResponseSchema }),
  setLineStatus: (id: string, body: LineStatusRequest) =>
    apiRequest(`/v1/admin/lines/${encodeURIComponent(id)}/status`, {
      method: 'POST',
      body,
      idempotent: true,
      // La API real devuelve solo la línea: la consola recarga el detalle.
      schema: z.union([AdminLineDetailSchema, CreditLineDetailSchema.transform(() => null)]),
    }),

  // Configuración
  getSetting: <K extends SettingKey>(key: K): Promise<Versioned<SettingValue<K>>> =>
    apiRequest(SETTING_PATHS[key], { schema: settingResponseSchema(key) }),
  putSetting: <K extends SettingKey>(
    key: K,
    value: SettingValue<K>,
    reason: string,
  ): Promise<Versioned<SettingValue<K>>> =>
    apiRequest(SETTING_PATHS[key], {
      method: 'PUT',
      body: RAW_SETTING_WIRE[key]?.write(value, reason) ?? { value, reason },
      idempotent: true,
      schema: settingResponseSchema(key),
    }),
  listUsuryRates: () => apiRequest('/v1/admin/usury-rates', { schema: UsuryRateListSchema }),
  previewDecisionPolicy: (policy: SettingValue<'decisionPolicy'>) =>
    apiRequest('/v1/admin/settings/decision-policy/preview', {
      method: 'POST',
      body: { policy },
      schema: DecisionPolicyPreviewSchema,
    }),

  // Expedientes (originación)
  listApplications: (query: AdminApplicationQuery) =>
    apiRequest('/v1/admin/applications', { query, schema: ApplicationListResponseSchema }),
  getApplication: (id: string) =>
    apiRequest(`/v1/admin/applications/${encodeURIComponent(id)}`, {
      schema: ApplicationDetailResponseSchema,
    }),
  assignApplication: (id: string) =>
    apiRequest(`/v1/admin/applications/${encodeURIComponent(id)}/assign`, {
      method: 'POST',
      body: {},
      schema: AdminApplicationDetailSchema,
    }),
  decideApplication: (id: string, body: z.input<typeof AdminDecisionRequestSchema>) =>
    apiRequest(`/v1/admin/applications/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body,
      idempotent: true,
      schema: DecisionResponseSchema,
    }),
  requestDocument: (id: string, body: { type: EvidenceType; message: string }) =>
    apiRequest(`/v1/admin/applications/${encodeURIComponent(id)}/request-document`, {
      method: 'POST',
      body,
      schema: AdminApplicationDetailSchema,
    }),
  returnToPhase: (id: string, body: { phase: ApplicationPhase; reason: string }) =>
    apiRequest(`/v1/admin/applications/${encodeURIComponent(id)}/return-to-phase`, {
      method: 'POST',
      body,
      schema: AdminApplicationDetailSchema,
    }),
  adjustCashFlowItem: (
    id: string,
    itemId: string,
    body: { monthlyAmount: MoneyJson; reason: string },
  ) =>
    apiRequest(
      `/v1/admin/applications/${encodeURIComponent(id)}/cash-flow/items/${encodeURIComponent(itemId)}`,
      { method: 'PATCH', body, schema: AdminApplicationDetailSchema },
    ),
  reviewDocument: (docId: string, body: { verdict: DocumentVerdict; reason: string }) =>
    apiRequest(`/v1/admin/documents/${encodeURIComponent(docId)}/review`, {
      method: 'PATCH',
      body,
      schema: AdminApplicationDetailSchema,
    }),

  // Dashboard
  getMetrics: (from: string, to?: string) =>
    apiRequest('/v1/admin/metrics', { query: { from, to }, schema: AdminMetricsSchema }),
  getIntegrations: () => apiRequest('/v1/admin/integrations', { schema: IntegrationHealthSchema }),

  // Disputas, devoluciones y conciliación (rutas reales de payments/backoffice)
  listDisputes: () => apiRequest('/v1/admin/disputes', { schema: DisputePageSchema }),
  decideDispute: (id: string, body: z.input<typeof DisputeDecisionRequestSchema>) =>
    apiRequest(`/v1/disputes/${encodeURIComponent(id)}/decision`, {
      method: 'POST',
      body,
      idempotent: true,
      schema: DisputeSchema,
    }),
  listIncomingRefunds: (matched?: boolean) =>
    apiRequest('/v1/admin/incoming-refunds', {
      query: { matched },
      schema: IncomingRefundPageSchema,
    }),
  matchRefund: (id: string, paymentIntentId: string, reason: string) =>
    apiRequest(`/v1/admin/incoming-refunds/${encodeURIComponent(id)}/match`, {
      method: 'POST',
      body: { paymentIntentId, reason },
      idempotent: true,
      schema: IncomingRefundSchema,
    }),
  listReconRuns: () =>
    apiRequest('/v1/admin/reconciliation/runs', {
      schema: z.object({ data: z.array(ReconciliationRunSchema) }),
    }),
  runReconciliation: (businessDate: string) =>
    apiRequest('/v1/admin/reconciliation/runs', {
      method: 'POST',
      body: { businessDate },
      idempotent: true,
      schema: ReconciliationRunSchema,
    }),
  listReconExceptions: (status?: 'OPEN' | 'RESOLVED') =>
    apiRequest('/v1/admin/reconciliation/exceptions', {
      query: { status },
      schema: z.object({ data: z.array(ReconciliationExceptionSchema) }),
    }),
  reconExceptionAction: (
    id: string,
    action: 'RETRY_STEP' | 'FORCE_COMPENSATION' | 'RESOLVE',
    reason: string,
  ) =>
    apiRequest(`/v1/admin/reconciliation/exceptions/${encodeURIComponent(id)}/actions`, {
      method: 'POST',
      body: { action, reason },
      idempotent: true,
      schema: ReconciliationExceptionSchema,
    }),

  // Agentes
  listAgentSessions: (query: AgentSessionQuery) =>
    apiRequest('/v1/admin/agent/sessions', { query, schema: AgentSessionListResponseSchema }),
  getAgentTrace: (id: string) =>
    apiRequest(`/v1/admin/agent/sessions/${encodeURIComponent(id)}/trace`, {
      schema: AgentTraceResponseSchema,
    }),
  takeOverSession: (id: string) =>
    apiRequest(`/v1/admin/agent/sessions/${encodeURIComponent(id)}/takeover`, { method: 'POST' }),
  sendHumanMessage: (id: string, text: string) =>
    apiRequest(`/v1/admin/agent/sessions/${encodeURIComponent(id)}/messages`, {
      method: 'POST',
      body: { text },
      schema: HumanMessageResultSchema,
    }),
  releaseSession: (id: string) =>
    apiRequest(`/v1/admin/agent/sessions/${encodeURIComponent(id)}/release`, { method: 'POST' }),
  killSession: (id: string, reason: string) =>
    apiRequest(`/v1/admin/agent/sessions/${encodeURIComponent(id)}/kill`, {
      method: 'POST',
      body: { reason },
    }),
  setKillSwitch: (agent: AgentRole, enabled: boolean, reason: string) =>
    apiRequest('/v1/admin/agent/kill-switch', {
      method: 'POST',
      body: { agent, enabled, reason },
    }),
  getAgentUsage: () => apiRequest('/v1/admin/agent/usage', { schema: AgentUsageSchema }),
  listHumanTasks: () =>
    apiRequest('/v1/admin/human-tasks', {
      schema: z.object({ data: z.array(HumanTaskSchema) }),
    }),
  getHumanTask: (id: string) =>
    apiRequest(`/v1/admin/human-tasks/${encodeURIComponent(id)}`, {
      schema: VersionedHumanTaskSchema,
    }),
  assignHumanTask: (id: string, version: number) =>
    apiRequest(`/v1/admin/human-tasks/${encodeURIComponent(id)}/assign`, {
      method: 'POST',
      body: { version },
      schema: HumanTaskSchema,
    }),
  resolveHumanTask: (id: string, version: number, resolution: Record<string, unknown>) =>
    apiRequest(`/v1/admin/human-tasks/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: { version, resolution },
      schema: HumanTaskSchema,
    }),
  getCustomerMemory: (customerId: string) =>
    apiRequest(`/v1/admin/customers/${encodeURIComponent(customerId)}/memory`, {
      schema: CustomerMemorySchema,
    }),
  rollbackMemory: (customerId: string, checkpointId: string, reason: string) =>
    apiRequest(`/v1/admin/customers/${encodeURIComponent(customerId)}/memory/rollback`, {
      method: 'POST',
      body: { checkpointId, reason },
    }),

  // Base de conocimiento y skills
  listKbArticles: () =>
    apiRequest('/v1/admin/knowledge/articles', {
      schema: z.object({ data: z.array(KbArticleSchema) }),
    }),
  getKbArticle: (id: string) =>
    apiRequest(`/v1/admin/knowledge/articles/${encodeURIComponent(id)}`, {
      schema: KbArticleSchema,
    }),
  createKbArticle: (body: z.input<typeof CreateKbArticleRequestSchema>) =>
    apiRequest('/v1/admin/knowledge/articles', { method: 'POST', body, schema: KbArticleSchema }),
  updateKbArticle: (id: string, body: z.input<typeof UpdateKbArticleRequestSchema>) =>
    apiRequest(`/v1/admin/knowledge/articles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      schema: KbArticleSchema,
    }),
  kbTransition: (id: string, action: 'submit-review' | 'publish' | 'archive') =>
    apiRequest(`/v1/admin/knowledge/articles/${encodeURIComponent(id)}/${action}`, {
      method: 'POST',
      schema: KbArticleSchema,
    }),
  listKbVersions: (id: string) =>
    apiRequest(`/v1/admin/knowledge/articles/${encodeURIComponent(id)}/versions`, {
      schema: z.object({ data: z.array(KbArticleVersionSchema) }),
    }),
  kbSearchPreview: (query: string) =>
    apiRequest('/v1/admin/knowledge/search-preview', {
      method: 'POST',
      body: { query, limit: 5 },
      schema: z.object({ data: z.array(KbSearchHitSchema) }),
    }),
  listSkills: () =>
    apiRequest('/v1/admin/skills', { schema: z.object({ data: z.array(AgentSkillSchema) }) }),
  getSkill: (code: string) =>
    apiRequest(`/v1/admin/skills/${encodeURIComponent(code)}`, { schema: AgentSkillSchema }),
  listSkillVersions: (code: string) =>
    apiRequest(`/v1/admin/skills/${encodeURIComponent(code)}/versions`, {
      schema: SkillVersionListSchema,
    }),
  createSkillVersion: (code: string, body: { contentMd: string; changeNote?: string }) =>
    apiRequest(`/v1/admin/skills/${encodeURIComponent(code)}/versions`, {
      method: 'POST',
      body,
      schema: AgentSkillSchema,
    }),
  activateSkill: (code: string, version: number) =>
    apiRequest(`/v1/admin/skills/${encodeURIComponent(code)}/activate`, {
      method: 'POST',
      body: { version },
      schema: AgentSkillSchema,
    }),

  // Auditoría
  listAudit: (query: AuditQuery) =>
    apiRequest('/v1/admin/audit', { query, schema: AuditPageSchema }),
  verifyAudit: (from: string | null, to: string | null) =>
    apiRequest('/v1/admin/audit/verify', {
      method: 'POST',
      body: { from, to },
      schema: AuditVerifyResultSchema,
    }),

  // Controles de demo
  getClock: () => apiRequest('/v1/demo/clock', { schema: DemoClockSchema }),
  advanceClock: (days: number) =>
    apiRequest('/v1/demo/clock/advance', {
      method: 'POST',
      body: { days },
      idempotent: true,
      schema: AdvanceClockResponseSchema,
    }),
  resetClock: () => apiRequest('/v1/demo/clock/reset', { method: 'POST', schema: DemoClockSchema }),
  getFaultMode: () => apiRequest('/v1/demo/breb/fault-mode', { schema: BrebFaultStateSchema }),
  setFaultMode: (body: { mode: string; count: number; latencyMs?: number }) =>
    apiRequest('/v1/demo/breb/fault-mode', {
      method: 'POST',
      body,
      schema: BrebFaultStateSchema,
    }),
  listDemoMerchants: () => apiRequest('/v1/demo/merchants', { schema: DemoMerchantListSchema }),
  generateQr: (body: { merchantAlias: string; amount?: MoneyJson; reference?: string }) =>
    apiRequest('/v1/demo/qr', { method: 'POST', body, schema: GenerateDemoQrResponseSchema }),
  merchantRefund: (body: { paymentIntentId: string; amount: MoneyJson }) =>
    apiRequest('/v1/demo/merchant-refunds', { method: 'POST', body, idempotent: true }),
  resetDemo: (body: { scope: 'ALL' | 'CUSTOMER'; customerId?: string }) =>
    apiRequest('/v1/demo/reset', {
      method: 'POST',
      body,
      idempotent: true,
      schema: DemoResetResultSchema,
    }),
  getGuide: () => apiRequest('/v1/demo/guide', { schema: DemoGuideSchema }),
};

export const queryKeys = {
  paymentIntents: (query: AdminPaymentIntentQuery) => ['payment-intents', query] as const,
  paymentIntent: (id: string) => ['payment-intent', id] as const,
  customers: (q: string, cursor?: string) => ['customers', q, cursor] as const,
  customer: (id: string) => ['customer', id] as const,
  line: (id: string) => ['line', id] as const,
  setting: (key: SettingKey) => ['setting', key] as const,
  usuryRates: ['usury-rates'] as const,
  applications: (query: AdminApplicationQuery) => ['applications', query] as const,
  application: (id: string) => ['application', id] as const,
  agentSessions: (query: AgentSessionQuery) => ['agent-sessions', query] as const,
  agentTrace: (id: string) => ['agent-trace', id] as const,
  agentUsage: ['agent-usage'] as const,
  metrics: (from: string) => ['metrics', from] as const,
  integrations: ['integrations'] as const,
  disputes: ['disputes'] as const,
  refunds: ['incoming-refunds'] as const,
  reconRuns: ['recon-runs'] as const,
  reconExceptions: ['recon-exceptions'] as const,
  humanTasks: ['human-tasks'] as const,
  memory: (customerId: string) => ['memory', customerId] as const,
  kbArticles: ['kb-articles'] as const,
  kbArticle: (id: string) => ['kb-article', id] as const,
  kbVersions: (id: string) => ['kb-versions', id] as const,
  skills: ['skills'] as const,
  skill: (code: string) => ['skill', code] as const,
  skillVersions: (code: string) => ['skill-versions', code] as const,
  audit: (query: AuditQuery) => ['audit', query] as const,
  clock: ['demo', 'clock'] as const,
  faultMode: ['demo', 'fault-mode'] as const,
  merchants: ['demo', 'merchants'] as const,
  guide: ['demo', 'guide'] as const,
};
